import {
  UseMutationResult,
  useQueryClient,
  useMutation,
  useInfiniteQuery,
  InfiniteData,
  useQuery,
  UseQueryResult,
  keepPreviousData,
} from '@tanstack/react-query';
import { useSprintPages } from './useSprintPages';
import { useOrganization } from './organization';

import * as api from '../api';
import * as queryKeys from '../constants/keys';

import type {
  AiSummaryInstructions,
  DraftSources,
  HourlyCost,
  IError,
  ListResponse,
  MembershipCreateValues,
  MembershipEntity,
  MembershipUpdateValues,
  PmMembersInfiniteData,
  PmMembersParams,
  PmParams,
  SprintEntity,
  UserEntity,
  MembershipStatus,
} from '../utils/types';

export const useMembership = (membershipId?: string): UseQueryResult<MembershipEntity, IError> =>
  useQuery({
    queryKey: [queryKeys.PM_MEMBERS, membershipId],
    queryFn: () => api.fetchMembership(membershipId!),
    enabled: !!membershipId,
    retry: false,
    refetchOnWindowFocus: false,
  });

/** Adding a colleague. They are a member the moment this resolves, so the list just refetches. */
export const useCreateMembership = (
  organizationId: string,
): UseMutationResult<MembershipEntity, IError, MembershipCreateValues, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: values => api.createMembership(organizationId, values),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [queryKeys.PM_MEMBERS] }),
  });
};

/**
 * One save for a person, however many of their fields changed. Invalidates the whole PM_MEMBERS key
 * rather than patching the cache by hand: a single request can change the name, the permissions and the
 * default chapter at once, and both the list and the person's page render all three.
 */
export const useUpdateMembership = (
  membershipId: string,
): UseMutationResult<MembershipEntity, IError, MembershipUpdateValues, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: values => api.updateMembership(membershipId, values),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [queryKeys.PM_MEMBERS] }),
  });
};

/**
 * Removes a membership outright. Invalidates the members list rather than patching a cache by hand: the
 * organization endpoint this used to edit optimistically does not send memberships at all.
 */
export const useRemoveMembership = (): UseMutationResult<void, IError, string, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (membershipId: string) => api.removeMembership(membershipId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [queryKeys.PM_MEMBERS] }),
  });
};

export const useInfiniteListPmMembers = (
  organizationId: string | undefined,
  params: PmMembersParams,
  enabled: boolean,
  sprints?: SprintEntity[],
) =>
  useInfiniteQuery<PmMembersInfiniteData, IError>({
    queryKey: [queryKeys.PM_MEMBERS_INFINITE, { organizationId, params }],
    queryFn: ({ pageParam }) => api.fetchPmMembersInfinite(organizationId!, params, { pageParam }),
    initialPageParam: undefined as unknown as {
      from?: Date | string;
      to?: Date | string;
      sprintNumber?: number;
      sprintId?: string;
    },
    getNextPageParam: (lastPage: {
      from: Date | string;
      to: Date | string;
      sprintNumber: number;
      sprintId: string;
    }) => {
      const lastPageFromDate = new Date(lastPage.from);
      const sortedSprints = (sprints ?? []).sort(
        (a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime(),
      );
      const nearestSprintIndex = sortedSprints.findIndex(sprint => new Date(sprint.endDate) >= lastPageFromDate);
      const sprint = nearestSprintIndex > 0 ? sortedSprints[nearestSprintIndex - 1] : undefined;
      if (!sprint) return undefined;
      return {
        from: sprint?.startDate,
        to: sprint?.endDate,
        sprintNumber: sprint?.number,
        sprintId: sprint?.id,
      };
    },
    placeholderData: keepPreviousData,
    enabled: enabled && !!organizationId,
  });
/**
 * `enabled` holds the read back until the caller's own inputs are known — Insights bounds its window by the
 * current sprint, and asking before that has loaded sends a window of `undefined` and caches the answer.
 */
export const useListPmMembers = (
  organizationId: string | undefined,
  params: PmMembersParams,
  enabled = true,
): UseQueryResult<ListResponse<MembershipEntity>, IError> =>
  useQuery({
    queryKey: [queryKeys.PM_MEMBERS, { organizationId, params }],
    queryFn: () => api.fetchPmMembers(organizationId!, params),
    enabled: enabled && !!organizationId,
  });

/**
 * Switches one person off or back on from their own page.
 *
 * Deliberately not `useUpdatePmMembers`: that one optimistically rewrites the members-list cache and reads
 * `old.items` without guarding it, so on a page that never listed members the callback throws and
 * react-query aborts the mutation before the request goes out. Here there is no list to patch — invalidate
 * and let it refetch.
 */
export const useUpdateUserStatus = (): UseMutationResult<
  UserEntity,
  IError,
  { userId: string; activeStatus: MembershipStatus },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, activeStatus }) => api.updateUser(userId, activeStatus),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [queryKeys.PM_MEMBERS] }),
  });
};

export const useUpdatePmMembers = (
  organizationId: string | undefined,
  params: PmMembersParams,
): UseMutationResult<UserEntity, IError, { userId: string; activeStatus: MembershipStatus }, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, activeStatus }) => api.updateUser(userId, activeStatus),
    onMutate: async ({ userId }) => {
      const previousUsers = queryClient.getQueryData([
        queryKeys.PM_MEMBERS,
        { organizationId, params },
      ]) as ListResponse<MembershipEntity>;

      queryClient.setQueryData(
        [queryKeys.PM_MEMBERS, { organizationId, params }],
        (old: ListResponse<MembershipEntity>) => ({
          ...old,
          items: old.items.filter(u => u.id !== userId),
        }),
      );

      return { previousUsers };
    },

    onError: (_, __, context) => {
      queryClient.setQueryData([queryKeys.PM_MEMBERS, { organizationId, params }], context?.previousUsers);
    },
  });
};

const updateMembershipPages = (
  members: InfiniteData<PmMembersInfiniteData>,
  values: Partial<MembershipEntity>,
  sprintId?: string,
) =>
  members.pages.map(page =>
    page.sprintId === sprintId
      ? {
          ...page,
          items: page.items.map(item => (item.id === values.id ? { ...item, ...values } : item)),
        }
      : page,
  );

export const useSendMembershipReminder = (
  organizationId?: string,
  params?: PmParams,
): UseMutationResult<void, IError, Partial<MembershipEntity>, unknown> => {
  const queryClient = useQueryClient();
  const queryKey = [queryKeys.PM_MEMBERS_INFINITE, { organizationId, params }];
  return useMutation({
    mutationFn: values => api.sendMembershipReminder(values.id),
    onMutate:
      params && organizationId
        ? async (values: Partial<MembershipEntity>) => {
            const previousPmMembers = queryClient.getQueryData(queryKey) as InfiniteData<PmMembersInfiniteData>;

            queryClient.setQueryData(queryKey, (members: InfiniteData<PmMembersInfiniteData>) => {
              const updatedPages = updateMembershipPages(members, values, params.sprintId);
              return { ...members, pages: updatedPages };
            });

            return { previousPmMembers };
          }
        : undefined,

    onError:
      params && organizationId
        ? (_, __, context) => {
            queryClient.setQueryData(queryKey, context?.previousPmMembers);
          }
        : undefined,
  });
};

/**
 * Turning Phill's daily message on or off for yourself.
 *
 * The organizations read is what carries your own membership into the store, so that is what has to be
 * refetched — the switch's value is a field on it, and leaving it stale would put the switch back where
 * it was the next time anything re-read it.
 *
 * Refused rather than sent while the membership is still in flight: the id is what the route is, so
 * without it the call would be a PATCH to `/memberships/undefined/daily-nudge` and the switch would
 * flick back with nothing to explain it.
 */
export const useSetDailyNudge = (
  membershipId?: string,
): UseMutationResult<MembershipEntity, IError, boolean, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (isDailyNudgeEnabled: boolean) =>
      membershipId
        ? api.setDailyNudge(membershipId, isDailyNudgeEnabled)
        : Promise.reject(new Error('No membership to set the daily nudge on')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [queryKeys.ORGANIZATIONS] }),
  });
};

export const useSetReminderTime = (
  membershipId?: string,
): UseMutationResult<MembershipEntity, IError, api.ReminderTimeInput, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: api.ReminderTimeInput) =>
      membershipId
        ? api.setReminderTime(membershipId, input)
        : Promise.reject(new Error('No membership to set the reminder time on')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [queryKeys.ORGANIZATIONS] }),
  });
};

/** Your own drafting instructions, from their own route: the organization read does not carry them. */
export const useAiSummaryInstructions = (membershipId?: string): UseQueryResult<AiSummaryInstructions, IError> =>
  useQuery({
    queryKey: [queryKeys.AI_SUMMARY_INSTRUCTIONS, membershipId],
    queryFn: () => api.getAiSummaryInstructions(membershipId as string),
    enabled: Boolean(membershipId),
  });

/** Same shape as the switch above, and its own read invalidated rather than the organizations one. */
export const useSetAiSummaryInstructions = (
  membershipId?: string,
): UseMutationResult<AiSummaryInstructions, IError, string, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (aiSummaryInstructions: string) =>
      membershipId
        ? api.setAiSummaryInstructions(membershipId, aiSummaryInstructions)
        : Promise.reject(new Error('No membership to set the drafting instructions on')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [queryKeys.AI_SUMMARY_INSTRUCTIONS, membershipId] }),
  });
};

/** Saved per switch, and written into the instructions read so the popover shows it at once. */
export const useSetDraftSources = (
  membershipId?: string,
): UseMutationResult<AiSummaryInstructions, IError, Partial<DraftSources>, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sources: Partial<DraftSources>) =>
      membershipId
        ? api.setDraftSources(membershipId, sources)
        : Promise.reject(new Error('No membership to set the draft sources on')),
    onSuccess: data => queryClient.setQueryData([queryKeys.AI_SUMMARY_INSTRUCTIONS, membershipId], data),
  });
};

/** A salary, so only asked for by a reader allowed to see it: the route refuses everyone else. */
export const useHourlyCost = (membershipId?: string, enabled = true): UseQueryResult<HourlyCost, IError> =>
  useQuery({
    queryKey: [queryKeys.HOURLY_COST, membershipId],
    queryFn: () => api.getHourlyCost(membershipId as string),
    enabled: enabled && Boolean(membershipId),
  });

/** Invalidates the product reads too, since an at-cost version prices off this figure. */
export const useSetHourlyCost = (
  membershipId?: string,
): UseMutationResult<HourlyCost, IError, number | null, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (hourlyCost: number | null) =>
      membershipId
        ? api.setHourlyCost(membershipId, hourlyCost)
        : Promise.reject(new Error('No membership to set the hourly cost on')),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.HOURLY_COST, membershipId] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.PRODUCT] });
    },
  });
};

/**
 * Your own default chapter, which is what every daily report of yours is filed under until you change it.
 *
 * The same shape as the switch above, and the same two reasons: it goes through an own-membership route
 * rather than the admin patch, and it invalidates the organizations read because that is what carries your
 * membership — and therefore your chapter — into the store.
 *
 * `null` clears the default and puts you back on the fallback.
 */
export const useSetDefaultChapter = (
  membershipId?: string,
): UseMutationResult<MembershipEntity, IError, string | null, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (chapter: string | null) =>
      membershipId
        ? api.setDefaultChapter(membershipId, chapter)
        : Promise.reject(new Error('No membership to set the default chapter on')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [queryKeys.ORGANIZATIONS] }),
  });
};

/**
 * One query per sprint of the open page, rather than one list that grows as you scroll.
 *
 * Each sprint keeps its own cache entry, so a sprint already opened comes back without asking again
 * and the current-sprint tab reuses what the past tab loaded. See useSprintPages.
 */
export const usePmMembersBySprint = (
  organizationId: string | undefined,
  sprints: SprintEntity[] | undefined,
  params: PmMembersParams,
  enabled: boolean,
) =>
  useSprintPages<MembershipEntity>(
    sprints,
    sprint => ({
      queryKey: [queryKeys.PM_MEMBERS_BY_SPRINT, { organizationId, sprintId: sprint.id, params }],
      queryFn: async () =>
        (
          await api.fetchPmMembersInfinite(organizationId!, params, {
            pageParam: { from: sprint.startDate, to: sprint.endDate, sprintNumber: sprint.number, sprintId: sprint.id },
          })
        ).items,
    }),
    enabled && !!organizationId,
  );

/** Named rather than `boolean | undefined`: two of three states would be falsy, and `if (!x)` would read "unknown" as "off". */
export type AiSummariesState = 'on' | 'off' | 'unknown';

/** This person's own membership, and whether Phill drafts their day. `unknown` until the answer is known. */
// Not the store's `organization.memberships`: the organizations *list* fills that with admins, so [0] is a colleague.
export const useOwnMembership = (
  organizationId: string | undefined,
  userId: string | undefined,
): { membership?: MembershipEntity; aiSummaries: AiSummariesState } => {
  const { data: organization, isPlaceholderData } = useOrganization(organizationId);
  // The placeholder comes from the organizations list, which carries only admins, so a non-admin finds no
  // row in it and would read as "summaries off" rather than as "not answered yet".
  const membership = isPlaceholderData
    ? undefined
    : (organization?.memberships ?? []).find(candidate => candidate.user?.id === userId);
  if (!membership) return { membership: undefined, aiSummaries: 'unknown' };

  return { membership, aiSummaries: membership.isDailyNudgeEnabled === true ? 'on' : 'off' };
};
